import React, { useRef } from 'react';
import {
  Camera,
  MapPin,
  Send,
  CheckCircle,
  X
} from 'lucide-react';

const IncidentFormModal = ({
  editingIncident,
  incidentForm,
  setIncidentForm,
  incidentImages,
  setIncidentImages,
  currentLocation,
  isLoading,
  error,
  success,
  onClose,
  onSubmit,
  onGetLocation
}) => {
  const fileInputRef = useRef(null);

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);

    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setIncidentImages(prev => [...prev, {
            id: `temp-${Date.now()}-${Math.random()}`,
            filename: file.name,
            content_type: file.type,
            size: file.size,
            data: e.target.result,
            temporary: true
          }]);
        };
        reader.readAsDataURL(file);
      }
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center p-2 sm:p-4 z-50 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto my-2 sm:my-0">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white pr-4">
            {editingIncident ? 'Edit Incident Report' : 'Report New Incident'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors flex-shrink-0"
          >
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {error && (
            <div className="mb-4 p-3 sm:p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-sm sm:text-base text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 sm:p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
              <p className="text-sm sm:text-base text-green-600 dark:text-green-400">{success}</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Incident Title *
                </label>
                <input
                  type="text"
                  value={incidentForm.title}
                  onChange={(e) => setIncidentForm(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-base sm:text-sm"
                  placeholder="Brief description of the incident"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Incident Type *
                </label>
                <select
                  value={incidentForm.incident_type}
                  onChange={(e) => setIncidentForm(prev => ({ ...prev, incident_type: e.target.value }))}
                  className="w-full px-3 py-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-base sm:text-sm"
                >
                  <option value="hazard">Hazard</option>
                  <option value="near_miss">Near Miss</option>
                  <option value="accident">Accident</option>
                  <option value="injury">Injury</option>
                  <option value="equipment_failure">Equipment Failure</option>
                  <option value="fire">Fire</option>
                  <option value="chemical_spill">Chemical Spill</option>
                  <option value="environmental">Environmental</option>
                  <option value="security">Security</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Severity Level *
                </label>
                <select
                  value={incidentForm.severity}
                  onChange={(e) => setIncidentForm(prev => ({ ...prev, severity: e.target.value }))}
                  className="w-full px-3 py-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-base sm:text-sm"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Location Description *
                </label>
                <input
                  type="text"
                  value={incidentForm.location_description}
                  onChange={(e) => setIncidentForm(prev => ({ ...prev, location_description: e.target.value }))}
                  className="w-full px-3 py-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-base sm:text-sm"
                  placeholder="Building, floor, room number, etc."
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button
                  type="button"
                  onClick={onGetLocation}
                  disabled={isLoading}
                  className="flex items-center justify-center px-4 py-3 sm:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-base sm:text-sm min-h-[44px] sm:min-h-0"
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  {isLoading ? 'Getting...' : 'Get GPS Location'}
                </button>

                {currentLocation && (
                  <div className="flex items-center text-sm text-green-600 dark:text-green-400 justify-center sm:justify-start">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Location captured
                  </div>
                )}
              </div>
            </div>

            {/* Description and Details */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Detailed Description *
                </label>
                <textarea
                  value={incidentForm.description}
                  onChange={(e) => setIncidentForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-base sm:text-sm resize-y"
                  placeholder="What happened? Provide as much detail as possible..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Immediate Actions Taken
                </label>
                <textarea
                  value={incidentForm.immediate_actions_taken}
                  onChange={(e) => setIncidentForm(prev => ({ ...prev, immediate_actions_taken: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-base sm:text-sm resize-y"
                  placeholder="What immediate actions were taken to address the incident?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Estimated Damage Cost (ZAR)
                </label>
                <input
                  type="number"
                  value={incidentForm.estimated_damage_cost || ''}
                  onChange={(e) => setIncidentForm(prev => ({ ...prev, estimated_damage_cost: e.target.value ? parseFloat(e.target.value) : null }))}
                  className="w-full px-3 py-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-base sm:text-sm"
                  placeholder="R 15,000.00"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>

          {/* Image Upload */}
          <div className="mt-4 sm:mt-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Evidence Photos
            </label>
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 sm:p-6 text-center">
              <Camera className="w-8 h-8 mx-auto text-gray-400 mb-2" />
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-3">
                Upload photos of the incident (optional)
              </p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-3 sm:py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 text-base sm:text-sm min-h-[44px] sm:min-h-0"
              >
                Choose Files
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>

            {incidentImages.length > 0 && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
                {incidentImages.map((img, index) => (
                  <div key={img.id || index} className="relative">
                    <img
                      src={img.data}
                      alt={`Evidence ${index + 1}`}
                      className="w-full h-20 sm:h-24 object-cover rounded-md"
                    />
                    <button
                      type="button"
                      onClick={() => setIncidentImages(prev => prev.filter((_, i) => i !== index))}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-7 h-7 sm:w-6 sm:h-6 flex items-center justify-center text-xs touch-manipulation"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row justify-end gap-3 sm:gap-0">
            <button
              type="submit"
              onClick={onSubmit}
              disabled={isLoading}
              className="w-full sm:w-auto px-6 py-3 sm:py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center justify-center text-base sm:text-sm min-h-[44px] sm:min-h-0"
            >
              <Send className="w-4 h-4 mr-2" />
              {isLoading ? (editingIncident ? 'Updating...' : 'Submitting...') : (editingIncident ? 'Update Incident' : 'Submit Incident Report')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncidentFormModal;
